import React, { Fragment, useState } from 'react';
import { bool, number, string, shape } from 'prop-types';
import dynamic from 'next/dynamic';
import { imageUrl } from 'util/images';
import Arrow from 'components/Icons/Arrow';
import ImageCaption from 'components/ImageCaption';
import Loading from 'components/Loading';
import PinItButton from 'components/PinItButton';
import PrismEntryButton from 'components/PrismEntryButton';
import { baseStyles, sliderHandleStyles } from './styles';

const ReactCompareImage = dynamic(() => import('react-compare-image'), {
  ssr: false,
  loading: () => <Loading height="350px" />
});

const SLIDER_HANDLE = (
  <div className="ImageSlider__handle">
    <div className="ImageSlider__handleArrow--left">
      <Arrow rotate={180} />
    </div>
    <div className="ImageSlider__handleDivider" />
    <div className="ImageSlider__handleArrow--right">
      <Arrow />
    </div>
    <style jsx>{sliderHandleStyles}</style>
  </div>
);

function ImageSlider(props) {
  const {
    image1,
    image2,
    post: { canonical },
    hasPrism
  } = props;

  const width = 1000;

  const { isPinnable, caption, credit, hashedPublicId } = image2;

  const [isShowingOverlay, setIsShowingOverlay] = useState(true);
  const [isAfterPictureVisible, setIsAfterPictureVisible] = useState(false);
  const [isBeforePictureVisible, setIsBeforePictureVisible] = useState(true);
  return (
    <Fragment>
      <style jsx>{baseStyles}</style>
      <div className="ImageSlider">
        <div className="ImageSlider__imagesContainer">
          <ReactCompareImage
            leftImage={imageUrl(image1.url, { width })}
            rightImage={imageUrl(image2.url, { width })}
            sliderLineWidth={3}
            sliderPositionPercentage={1}
            onSliderPositionChange={position => {
              if (isShowingOverlay) {
                setIsShowingOverlay(false);
              }
              if (image1.isPinnable) {
                setIsBeforePictureVisible(position > 0.1);
              }
              if (image2.isPinnable) {
                setIsAfterPictureVisible(position < 0.9);
              }
              if (position > 0.99) {
                setIsShowingOverlay(true);
              }
            }}
            handle={SLIDER_HANDLE}
          />
          {isBeforePictureVisible && image1.isPinnable && canonical && (
            <div className="ImageSlider__pinItButton--left">
              <PinItButton
                pageUrl={canonical}
                imageUrl={imageUrl(image1.url, { width })}
                description={image1.alt || 'Post Image'}
                placement="inline"
              />
            </div>
          )}
          {isAfterPictureVisible && isPinnable && canonical && (
            <div className="ImageSlider__pinItButton--right">
              <PinItButton
                pageUrl={canonical}
                imageUrl={imageUrl(image2.url, { width })}
                description={image2.alt || 'Post Image'}
                placement="inline"
              />
            </div>
          )}
          {isShowingOverlay && (
            <div className="ImageSlider__ctaOverlay">
              <span className="ImageSlider__ctaOverlayText">
                Slide to reveal
              </span>
            </div>
          )}
        </div>
        {(caption || credit || (hasPrism && hashedPublicId)) && (
          <div className="ImageSlider__imageInfo">
            {hasPrism && hashedPublicId && (
              <PrismEntryButton
                imageId={hashedPublicId}
                entryPageUrl={canonical}
              />
            )}
            {(caption || credit) && (
              <ImageCaption
                credit={`${credit}${
                  image2.credit
                    ? `, ${image2.credit.replace('Credit: ', '')}`
                    : ''
                }`}
                caption={caption && caption.replace(credit, '').trim()}
              />
            )}
          </div>
        )}
      </div>
    </Fragment>
  );
}

const imageType = shape({
  url: string,
  alt: string,
  caption: string,
  href: string,
  isPinnable: bool,
  originalWidth: number,
  originalHeight: number
});

ImageSlider.defaultProps = {
  image1: {},
  image2: {},
  post: undefined,
  hasPrism: false
};

ImageSlider.propTypes = {
  image1: imageType,
  image2: imageType,
  post: shape({
    canonical: string
  }),
  hasPrism: bool
};

export default ImageSlider;
